/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["item"] }] */
import get from 'lodash/get';
import { editorStateForEditorComponent } from 'Shared/utils/draftJsHelpers';
import { DragSourceMonitor, DropTargetMonitor } from 'react-dnd';

export function actionItemCanDrag(forbidDrag: boolean) {
  return !forbidDrag;
}

type BeginDragType = {
  uuid: string;
  getState: Function;
  selector?: string;
};

type BeginDataType = {
  dragedItemUuid: string;
  dragedItemIsHighPriority: boolean;
  dragedItemIsAssignPersonId: number;
  dragUuid: Array<string>;
  body: any;
};

export function actionItemBeginDrag({ uuid: itemUuid, getState, selector = 'meetingItems' }: BeginDragType) {
  const items = get(getState(), selector);
  const data = items.find(({ uuid }) => uuid === itemUuid);
  return {
    dragedItemUuid: itemUuid,
    dragedItemIsHighPriority: data.isHighPriority,
    dragedItemIsAssignPersonId: data.assignmentPersonId,
    dragUuid: [itemUuid],
    body: editorStateForEditorComponent(data.body),
  } as BeginDataType;
}

type DragPropsType = {
  onDrop: Function;
};

type EndDragType = {
  dragedItemUuid: string;
  hoverOffset: number;
  isDropOnEmptyArea: boolean;
};

export function actionItemEndDrag(
  { onDrop }: DragPropsType,
  monitor: DragSourceMonitor,
  { dragedItemUuid, hoverOffset, isDropOnEmptyArea }: EndDragType
) {
  if (isDropOnEmptyArea) {
    onDrop({
      draggedItemUuids: [dragedItemUuid],
      hoverOffset,
    });
  }
  const dropResult = monitor.getDropResult();
  if (!dropResult) return;
  const { hoveredItemUuid } = dropResult;
  if (!onDrop) return;
  onDrop({
    draggedItemUuids: [dragedItemUuid],
    hoveredItemUuid,
    hoverOffset,
  });
}

type DataType = {
  uuid: string;
};

export function actionItemIsDragging({ uuid }: DataType, monitor: DragSourceMonitor) {
  const { dragedItemUuid } = monitor.getItem();
  return dragedItemUuid === uuid;
}

export function actionItemDrop({ uuid }: DataType, monitor: DropTargetMonitor) {
  if (monitor.didDrop()) return undefined;
  return { hoveredItemUuid: uuid };
}

type HoverType = {
  containerRef: {
    current: HTMLElement;
  };
  setHoverOffset: Function;
  isLast: boolean;
};

export function actionItemHover(
  { containerRef, setHoverOffset, isLast }: HoverType,
  monitor: DropTargetMonitor,
  item: any
) {
  if (!containerRef && !containerRef.current) return;
  const node = containerRef.current;
  if (!monitor.canDrop()) return;

  const hoverBoundingRect = node.getBoundingClientRect();
  const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
  const clientOffset = monitor.getClientOffset();
  const hoverClientY = clientOffset.y - hoverBoundingRect.top;
  const hoverOffset = hoverClientY < hoverMiddleY ? 0 : 1;

  setHoverOffset(hoverOffset);

  if (isLast && hoverOffset) item.isDropOnEmptyArea = true;
  else if (item.isDropOnEmptyArea) item.isDropOnEmptyArea = false;

  item.hoverOffset = hoverOffset;
}

type DropDataType = {
  uuid: string;
  isHighPriority: boolean;
  assignmentPersonId: number;
};

type DragedItemDataType = {
  dragedItemUuid: string;
  dragedItemIsHighPriority: boolean;
  dragedItemIsAssignPersonId: number;
};

export function actionItemCanDrop(
  { uuid, isHighPriority, assignmentPersonId }: DropDataType,
  { dragedItemUuid, dragedItemIsHighPriority, dragedItemIsAssignPersonId }: DragedItemDataType
) {
  return (
    dragedItemIsHighPriority === isHighPriority &&
    dragedItemIsAssignPersonId === assignmentPersonId &&
    dragedItemUuid !== uuid
  );
}
